import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchTenantBranding,
  patchTenantBranding,
  resetTenantBranding,
  setBrandingAccess,
  uploadTenantLogo,
  type BrandingPatch,
  type TenantBranding,
} from "@/api/branding";
import { WHATSAPP_BILLING_KEYS } from "@/hooks/useWhatsappBilling";
import { useUIStore } from "@/store/useUIStore";

export const TENANT_BRANDING_KEY = ["tenantBranding"] as const;

/** This workspace's branding. The query cache is cleared on every account switch
 *  (AppProviders), so a key without the account id cannot serve one tenant's brand to another. */
export function useTenantBranding(enabled = true) {
  return useQuery<TenantBranding>({
    queryKey: TENANT_BRANDING_KEY,
    queryFn: fetchTenantBranding,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Every mutation below answers with the new effective branding. Writing it straight into the
 *  cache AND the store repaints the app immediately instead of after a refetch. */
function useApplyResult() {
  const queryClient = useQueryClient();
  const setTenantPalette = useUIStore((s) => s.setTenantPalette);
  return (result: TenantBranding) => {
    queryClient.setQueryData(TENANT_BRANDING_KEY, result);
    setTenantPalette(result.palette);
  };
}

export function useUpdateBranding() {
  const apply = useApplyResult();
  return useMutation<TenantBranding, Error, BrandingPatch>({
    mutationFn: patchTenantBranding,
    onSuccess: apply,
  });
}

export function useUploadLogo() {
  const apply = useApplyResult();
  return useMutation<TenantBranding, Error, File>({
    mutationFn: uploadTenantLogo,
    onSuccess: apply,
  });
}

export function useResetBranding() {
  const apply = useApplyResult();
  return useMutation<TenantBranding, Error, void>({
    mutationFn: () => resetTenantBranding(),
    onSuccess: apply,
  });
}

/** A platform admin granting or revoking one tenant's white-label entitlement. */
export function useSetBrandingAccess() {
  const queryClient = useQueryClient();
  return useMutation<TenantBranding, Error, { workspaceId: string; enabled: boolean }>({
    mutationFn: ({ workspaceId, enabled }) => setBrandingAccess(workspaceId, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminTenants() });
    },
  });
}
